import { useState } from "react";
import { useQueryReport, type GroupedResult } from "../hooks/useQueryReport";
import { useSavedReports, type ReportConfig, type SavedReport } from "../hooks/useSavedReports";
import { useVisibleTeams } from "@/hooks/useVisibleTeams";
import { SvgLineChart } from "../components/SvgLineChart";
import { useJobTypes } from "../../admin/hooks/useJobTypes";
import { SchedulePanel } from "./SchedulePanel";
import "./ReportBuilderPage.css";

const GROUP_BY_OPTIONS = [
  { value: "", label: "No grouping (raw records)" },
  { value: "classification", label: "Classification" },
  { value: "jobTypeId", label: "Job Type" },
  { value: "primaryOperator", label: "Operator" },
  { value: "team", label: "Team" },
  { value: "exportStatus", label: "Export Status" },
  { value: "exportDestination", label: "Export Destination" },
];

const groupLabel = (g: GroupedResult, groupBy: string) =>
  String((groupBy === "team" ? g.teamName : undefined) ?? g[groupBy] ?? "—");

const SORT_OPTIONS = [
  { value: "completedAt", label: "Completed At" },
  { value: "totalDurationMs", label: "Duration" },
  { value: "fieldCount", label: "Field Count" },
  { value: "correctionCount", label: "Corrections" },
];

function getDefaultFrom(): string {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return d.toISOString().slice(0, 10);
}

interface SavedReportEditFormProps {
  report: SavedReport;
  onSave: (data: { name: string; description: string; isShared: boolean }) => Promise<void>;
  onCancel: () => void;
}

// Task 9 (S-2) — saved-reports UX residue: rename/describe/re-share via the
// existing (already-live) PATCH /analytics/saved-reports/:id. Same
// expand-in-place idiom as SchedulePanel.tsx (Task 8) so the sidebar doesn't
// grow two different inline-edit patterns — per conductor/DESIGN.md's
// edit-as-primary exception, the editor shows all current values, not just
// empty fields.
function SavedReportEditForm({ report, onSave, onCancel }: SavedReportEditFormProps) {
  const [name, setName] = useState(report.name);
  const [description, setDescription] = useState(report.description ?? "");
  const [isShared, setIsShared] = useState(report.isShared);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setSaving(true);
    try {
      await onSave({ name: trimmed, description, isShared });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rbp-edit-panel">
      <div className="rbp-edit-row">
        <label className="rbp-label" htmlFor={`report-edit-name-${report.id}`}>Name</label>
        <input
          id={`report-edit-name-${report.id}`}
          className="rbp-input"
          data-testid="report-edit-name-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </div>
      <div className="rbp-edit-row">
        <label className="rbp-label" htmlFor={`report-edit-description-${report.id}`}>Description</label>
        <textarea
          id={`report-edit-description-${report.id}`}
          className="rbp-input rbp-edit-textarea"
          data-testid="report-edit-description-input"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
        />
      </div>
      <label className="rbp-edit-checkbox-row">
        <input
          type="checkbox"
          data-testid="report-edit-shared-checkbox"
          checked={isShared}
          onChange={(e) => setIsShared(e.target.checked)}
        />
        Shared
      </label>
      <div className="rbp-edit-actions">
        <button
          type="button"
          className="rbp-btn rbp-btn-primary"
          data-testid="report-edit-save-btn"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? "Saving…" : "Save"}
        </button>
        <button
          type="button"
          className="rbp-btn"
          data-testid="report-edit-cancel-btn"
          onClick={onCancel}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

export function ReportBuilderPage() {
  const [config, setConfig] = useState<ReportConfig>({
    groupBy: "",
    from: getDefaultFrom(),
    to: new Date().toISOString().slice(0, 10),
    sortBy: "completedAt",
    sortDir: "desc",
  });
  const [saveName, setSaveName] = useState("");
  const [showSave, setShowSave] = useState(false);
  const [scheduleOpenId, setScheduleOpenId] = useState<string | null>(null);
  const [editOpenId, setEditOpenId] = useState<string | null>(null);

  const { result, isLoading, error, runQuery } = useQueryReport();
  const { reports, save, update, remove } = useSavedReports();
  const { items: jobTypes } = useJobTypes();
  const { teams } = useVisibleTeams();

  const setField = (key: keyof ReportConfig, value: string) => {
    setConfig((prev) => ({ ...prev, [key]: value || undefined }));
  };

  const handleRun = () => runQuery(config);

  const handleSave = async () => {
    if (!saveName.trim()) return;
    await save(saveName.trim(), config);
    setSaveName("");
    setShowSave(false);
  };

  const handleLoad = (report: (typeof reports)[0]) => {
    setConfig(report.config);
    runQuery(report.config);
  };

  const handleEditSave = async (
    id: string,
    data: { name: string; description: string; isShared: boolean },
  ) => {
    await update(id, data);
    setEditOpenId(null);
  };

  return (
    <div className="rbp-page">
      <div className="rbp-header">
        <h1 className="rbp-title">Report Builder</h1>
        <div className="rbp-header-actions">
          {!showSave ? (
            <button className="rbp-btn" onClick={() => setShowSave(true)}>Save Report</button>
          ) : (
            <div className="rbp-save-inline">
              <input
                className="rbp-input rbp-save-input"
                placeholder="Report name..."
                value={saveName}
                onChange={(e) => setSaveName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSave()}
                autoFocus
              />
              <button className="rbp-btn rbp-btn-primary" onClick={handleSave}>Save</button>
              <button className="rbp-btn" onClick={() => setShowSave(false)}>Cancel</button>
            </div>
          )}
        </div>
      </div>

      <div className="rbp-layout">
        {/* Sidebar: saved reports */}
        <aside className="rbp-sidebar">
          <div className="rbp-sidebar-title">Saved Reports</div>
          {reports.length === 0 ? (
            <div className="rbp-sidebar-empty">No saved reports yet</div>
          ) : (
            <ul className="rbp-report-list">
              {reports.map((r) => (
                <li key={r.id} className="rbp-report-item-wrap">
                  <div className="rbp-report-item">
                    <button className="rbp-report-name" onClick={() => handleLoad(r)}>
                      {r.name}
                      {r.isShared && <span className="rbp-shared-badge">shared</span>}
                    </button>
                    <button
                      className="rbp-report-edit-toggle"
                      data-testid={`report-edit-toggle-${r.id}`}
                      onClick={() => setEditOpenId((prev) => (prev === r.id ? null : r.id))}
                      title="Edit"
                      aria-label={`Edit ${r.name}`}
                    >
                      ✎
                    </button>
                    <button
                      className="rbp-report-schedule-toggle"
                      data-testid={`schedule-toggle-${r.id}`}
                      onClick={() => setScheduleOpenId((prev) => (prev === r.id ? null : r.id))}
                      title="Schedule"
                    >
                      Schedule
                    </button>
                    <button className="rbp-report-delete" onClick={() => remove(r.id)} title="Delete">&times;</button>
                  </div>
                  {editOpenId === r.id && (
                    <SavedReportEditForm
                      report={r}
                      onSave={(data) => handleEditSave(r.id, data)}
                      onCancel={() => setEditOpenId(null)}
                    />
                  )}
                  {scheduleOpenId === r.id && <SchedulePanel reportId={r.id} />}
                </li>
              ))}
            </ul>
          )}
        </aside>

        {/* Main area */}
        <main className="rbp-main">
          {/* Query controls */}
          <div className="rbp-controls">
            <div className="rbp-row">
              <label className="rbp-label">Group By</label>
              <select className="rbp-select" value={config.groupBy ?? ""} onChange={(e) => setField("groupBy", e.target.value)}>
                {GROUP_BY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>

            <div className="rbp-row">
              <label className="rbp-label">Job Type</label>
              <select className="rbp-select" value={config.jobTypeId ?? ""} onChange={(e) => setField("jobTypeId", e.target.value)}>
                <option value="">All</option>
                {jobTypes.map((jt) => (
                  <option key={jt.id} value={jt.id}>{jt.name}</option>
                ))}
              </select>
            </div>

            <div className="rbp-row">
              <label className="rbp-label">From</label>
              <input className="rbp-input" type="date" value={config.from ?? ""} onChange={(e) => setField("from", e.target.value)} />
            </div>

            <div className="rbp-row">
              <label className="rbp-label">To</label>
              <input className="rbp-input" type="date" value={config.to ?? ""} onChange={(e) => setField("to", e.target.value)} />
            </div>

            <div className="rbp-row">
              <label className="rbp-label">Classification</label>
              <input className="rbp-input" value={config.classification ?? ""} onChange={(e) => setField("classification", e.target.value)} placeholder="Filter..." />
            </div>

            <div className="rbp-row">
              <label className="rbp-label">Operator</label>
              <input className="rbp-input" value={config.operator ?? ""} onChange={(e) => setField("operator", e.target.value)} placeholder="User ID..." />
            </div>

            {teams.length > 0 && (
              <div className="rbp-row">
                <label className="rbp-label">Team</label>
                <select className="rbp-select" value={config.teamId ?? ""} onChange={(e) => setField("teamId", e.target.value)}>
                  <option value="">All</option>
                  {teams.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>
            )}

            {!config.groupBy && (
              <div className="rbp-row">
                <label className="rbp-label">Sort By</label>
                <select className="rbp-select" value={config.sortBy ?? "completedAt"} onChange={(e) => setField("sortBy", e.target.value)}>
                  {SORT_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                <select className="rbp-select rbp-sort-dir" value={config.sortDir ?? "desc"} onChange={(e) => setField("sortDir", e.target.value)}>
                  <option value="desc">Desc</option>
                  <option value="asc">Asc</option>
                </select>
              </div>
            )}

            <button className="rbp-btn rbp-btn-primary rbp-run-btn" onClick={handleRun} disabled={isLoading}>
              {isLoading ? "Running..." : "Run Query"}
            </button>
          </div>

          {/* Results */}
          {error && <div className="rbp-error">{error}</div>}

          {result && (
            <div className="rbp-results">
              <div className="rbp-results-header">
                <span className="rbp-results-count">
                  {result.total} {result.groupBy ? "groups" : "records"}
                </span>
              </div>

              {/* Chart for grouped results */}
              {result.grouped && result.grouped.length > 0 && (
                <div className="rbp-chart-area">
                  <SvgLineChart
                    data={result.grouped.map((g) => ({
                      label: groupLabel(g, result.groupBy!),
                      value: g._count.id,
                    }))}
                    color="#ea580c"
                    height={200}
                  />
                </div>
              )}

              {/* Grouped results table */}
              {result.grouped && (
                <table className="rbp-table">
                  <thead>
                    <tr>
                      <th>{result.groupBy}</th>
                      <th>Count</th>
                      <th>Avg Duration</th>
                      <th>Avg Fields</th>
                      <th>Total Corrections</th>
                      <th>Total Overrides</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.grouped.map((g, i) => (
                      <tr key={i}>
                        <td className="rbp-cell-primary">{groupLabel(g, result.groupBy!)}</td>
                        <td>{g._count.id}</td>
                        <td>{g._avg.totalDurationMs != null ? formatMs(g._avg.totalDurationMs) : "—"}</td>
                        <td>{g._avg.fieldCount?.toFixed(1) ?? "—"}</td>
                        <td>{g._sum.correctionCount ?? 0}</td>
                        <td>{g._sum.overrideCount ?? 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {/* Raw results table */}
              {result.raw && (
                <table className="rbp-table">
                  <thead>
                    <tr>
                      <th>Completed</th>
                      <th>Classification</th>
                      <th>Duration</th>
                      <th>Fields</th>
                      <th>Corrections</th>
                      <th>Overrides</th>
                      <th>SLA</th>
                      <th>Export</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.raw.map((r) => (
                      <tr key={r.id}>
                        <td>{new Date(r.completedAt).toLocaleDateString()}</td>
                        <td className="rbp-cell-primary">{r.classification ?? "—"}</td>
                        <td>{formatMs(r.totalDurationMs)}</td>
                        <td>{r.fieldCount}</td>
                        <td>{r.correctionCount}</td>
                        <td>{r.overrideCount}</td>
                        <td className={r.slaMet === true ? "rbp-cell-good" : r.slaMet === false ? "rbp-cell-bad" : ""}>
                          {r.slaMet === true ? "Met" : r.slaMet === false ? "Missed" : "—"}
                        </td>
                        <td>{r.exportStatus ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

function formatMs(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60000).toFixed(1)}m`;
}
